import React, { useState, useEffect, useCallback } from 'react';
import { documentosAPI, clientesAPI, emailAPI } from '../services/api';
import DocumentoForm from './DocumentoForm';
import './Documentos.css';

function Documentos() {
  const [documentos, setDocumentos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroPlantilla, setFiltroPlantilla] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [documentoParaEmail, setDocumentoParaEmail] = useState(null);
  const [formEmail, setFormEmail] = useState({ email: '', mensaje: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);

  const loadClientes = useCallback(async () => {
    try {
      const response = await clientesAPI.getAll(true);
      setClientes(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadPlantillas = useCallback(async () => {
    try {
      const response = await documentosAPI.getPlantillas(true);
      setPlantillas(response.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadDocumentos = useCallback(async () => {
    try {
      setLoading(true);
      const clienteId = filtroCliente ? parseInt(filtroCliente) : null;
      const response = await documentosAPI.getDocumentos(clienteId);
      // Asegurar que siempre sea un array
      const documentosData = Array.isArray(response.data) ? response.data : [];
      setDocumentos(documentosData);
      setError(null);
    } catch (err) {
      setError('Error al cargar los documentos');
      console.error('Error loading documentos:', err.message);
      setDocumentos([]); // Asegurar que siempre sea un array incluso en caso de error
    } finally {
      setLoading(false);
    }
  }, [filtroCliente]);

  useEffect(() => {
    loadClientes();
    loadPlantillas();
    loadDocumentos();
  }, [loadClientes, loadPlantillas, loadDocumentos]);

  useEffect(() => {
    loadDocumentos();
  }, [loadDocumentos]);

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellidos}` : 'Cliente no encontrado';
  };

  const getPlantillaNombre = (plantillaId) => {
    const plantilla = plantillas.find(p => p.id === plantillaId);
    return plantilla ? plantilla.nombre : 'Plantilla no encontrada';
  };

  const handleGenerar = () => {
    if (plantillas.length === 0) {
      setError('No hay plantillas disponibles. Primero crea una plantilla.');
      if (window.confirm('¿Quieres ir a la página de plantillas para crear una?')) {
        window.location.href = '/plantillas';
      }
      return;
    }
    setShowForm(true);
  };

  const handleDownload = async (documentoId, nombreArchivo) => {
    try {
      const response = await documentosAPI.downloadDocumento(documentoId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar el documento');
      console.error(err);
    }
  };

  const handleDownloadPDF = async (documentoId, nombreArchivo) => {
    try {
      const nombreArchivoPdf = nombreArchivo.replace('.docx', '.pdf');
      const response = await documentosAPI.downloadDocumentoPDF(documentoId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivoPdf);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar el PDF. Puede que el PDF no esté disponible para este documento.');
      console.error(err);
    }
  };

  const handleAbrirEnviarEmail = (documento) => {
    const cliente = clientes.find(c => c.id === documento.cliente_id);
    setDocumentoParaEmail(documento);
    setFormEmail({
      email: cliente?.email || '',
      mensaje: '',
    });
    setEmailError(null);
    setEmailSuccess(null);
    setShowEmailModal(true);
  };

  const handleEnviarEmailSubmit = async () => {
    if (!documentoParaEmail) return;
    if (!formEmail.email || !formEmail.email.includes('@')) {
      setEmailError('Introduce un email válido.');
      return;
    }
    setEmailError(null);
    setEmailSending(true);
    try {
      const response = await emailAPI.send('documento', documentoParaEmail.id, formEmail.email, formEmail.mensaje);
      if (response.data?.success) {
        setEmailSuccess('Documento enviado por email correctamente.');
        setTimeout(() => {
          setShowEmailModal(false);
          setDocumentoParaEmail(null);
          setEmailSuccess(null);
        }, 1500);
      } else {
        setEmailError(response.data?.error || 'Error al enviar el email.');
      }
    } catch (e) {
      setEmailError(e.response?.data?.error || 'Error al enviar el email.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
      try {
        await documentosAPI.deleteDocumento(id);
        setSuccess('Documento eliminado correctamente');
        loadDocumentos();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError('Error al eliminar el documento');
        console.error(err);
      }
    }
  };

  const handleFormClose = () => {
    setError(null); // Limpiar error al cerrar
    setShowForm(false);
  };

  const handleFormSubmit = async (data) => {
    try {
      setError(null); // Limpiar errores generales
      const response = await documentosAPI.generarDocumento(data);
      
      // Parsear JSON si viene como string (debido a warnings de PHP)
      let responseData = response.data || response;
      if (typeof responseData === 'string') {
        try {
          // Buscar el JSON en el string (puede haber warnings antes)
          const jsonMatch = responseData.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            responseData = JSON.parse(jsonMatch[0]);
          }
        } catch (e) {
          console.error('Error al parsear JSON:', e);
        }
      }
      
      // Mensaje de éxito con información sobre PDF
      const pdfGenerado = responseData?.ruta_archivo_pdf ? ' y PDF' : '';
      setSuccess(`Documento Word${pdfGenerado} generado correctamente`);
      setShowForm(false); // Cerrar modal en caso de éxito
      
      // Esperar un momento antes de recargar para asegurar que el documento se haya guardado
      setTimeout(() => {
        loadDocumentos();
      }, 500);
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      // Extraer mensaje de error del servidor
      let errorMessage = 'Error al generar el documento';
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        if (errorData.error) {
          errorMessage = errorData.error;
          
          // Si el error menciona que no se encuentra la plantilla, hacer el mensaje más claro
          if (errorMessage.includes('no encontrado') || errorMessage.includes('no encontrada')) {
            errorMessage = '⚠️ ' + errorMessage + '. Por favor, verifica que el archivo de la plantilla existe en el servidor.';
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Cerrar el modal inmediatamente cuando hay error
      setShowForm(false);
      setError(errorMessage);
    }
  };

  // Función para filtrar documentos por plantilla
  const getDocumentosFiltrados = () => {
    let filtrados = documentos;
    
    // Filtro por plantilla
    if (filtroPlantilla) {
      filtrados = filtrados.filter(documento => {
        return documento.plantilla_id === parseInt(filtroPlantilla);
      });
    }
    
    return filtrados;
  };

  if (loading) {
    return <div className="loading">Cargando documentos...</div>;
  }

  const documentosFiltrados = getDocumentosFiltrados();

  return (
    <div className="documentos">
      <div className="page-header">
        <h2>Gestión de Documentos</h2>
        <button className="btn btn-primary" onClick={handleGenerar}>
          Generar Documento
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} title="Cerrar">×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)} title="Cerrar">×</button>
        </div>
      )}

      <div className="filtros">
        <div className="filtros-grid">
          <div className="form-group">
            <label>Filtrar por Cliente:</label>
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
            >
              <option value="">Todos los clientes</option>
              {clientes.map(cliente => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nombre} {cliente.apellidos}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrar por Plantilla:</label>
            <select
              value={filtroPlantilla}
              onChange={(e) => setFiltroPlantilla(e.target.value)}
            >
              <option value="">Todas las plantillas</option>
              {plantillas.map(plantilla => (
                <option key={plantilla.id} value={plantilla.id}>
                  {plantilla.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="documentos-list">
        {Array.isArray(documentosFiltrados) && documentosFiltrados.map(documento => {
          const fechaGen = new Date(documento.fecha_generacion);
          return (
            <div key={documento.id} className="documento-card">
              <div className="documento-header">
                <div>
                  <h3>{documento.nombre_archivo}</h3>
                  <p className="documento-info">
                    Cliente: {getClienteNombre(documento.cliente_id)} | 
                    Plantilla: {getPlantillaNombre(documento.plantilla_id)}
                  </p>
                  <p className="documento-fecha">
                    Generado: {fechaGen.toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              {documento.notas && (
                <div className="documento-notas">
                  <p><strong>Notas:</strong> {documento.notas}</p>
                </div>
              )}
              <div className="documento-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleDownload(documento.id, documento.nombre_archivo)}
                  title="Descargar Word"
                >
                  Descargar Word
                </button>
                {documento.ruta_archivo_pdf && (
                  <>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleDownloadPDF(documento.id, documento.nombre_archivo)}
                      title="Descargar PDF"
                    >
                      Descargar PDF
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleAbrirEnviarEmail(documento)}
                      title="Enviar por email (con PDF adjunto)"
                    >
                      📧 Enviar por email
                    </button>
                  </>
                )}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(documento.id)}
                >
                  Eliminar
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {documentos.length === 0 && (
        <div className="empty-state">
          <p>No hay documentos generados. Genera el primero haciendo clic en "Generar Documento".</p>
        </div>
      )}
      {documentos.length > 0 && documentosFiltrados.length === 0 && (
        <div className="empty-state">
          <p>No hay documentos que coincidan con los filtros seleccionados.</p>
        </div>
      )}

      {showForm && (
        <DocumentoForm
          clientes={clientes}
          plantillas={plantillas}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      {showEmailModal && (
        <div className="email-modal-overlay" onClick={() => !emailSending && setShowEmailModal(false)}>
          <div className="email-modal-box" onClick={e => e.stopPropagation()}>
            <h3>Enviar documento por email</h3>
            {documentoParaEmail && (
              <>
                <p className="email-modal-cliente">Cliente: <strong>{getClienteNombre(documentoParaEmail.cliente_id)}</strong></p>
                <p className="email-modal-doc-name">Se enviará el PDF: <strong>{documentoParaEmail.nombre_archivo}</strong></p>
              </>
            )}
            {emailError && <div className="alert alert-error">{emailError}</div>}
            {emailSuccess && <div className="alert alert-success">{emailSuccess}</div>}
            <div className="form-group">
              <label>Email del destinatario *</label>
              <input
                type="email"
                value={formEmail.email}
                onChange={e => setFormEmail(f => ({ ...f, email: e.target.value }))}
                placeholder="cliente@email.com"
                disabled={emailSending}
              />
            </div>
            <div className="form-group">
              <label>Mensaje (opcional)</label>
              <textarea
                value={formEmail.mensaje}
                onChange={e => setFormEmail(f => ({ ...f, mensaje: e.target.value }))}
                placeholder="Texto que aparecerá en el email..."
                rows={3}
                disabled={emailSending}
              />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowEmailModal(false)} disabled={emailSending}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={handleEnviarEmailSubmit} disabled={emailSending}>
                {emailSending ? 'Enviando...' : '📧 Enviar email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Documentos;

